import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  Input,
  Modal,
  Row,
  Spin,
  Tag,
  Descriptions,
  Statistic,
} from "antd";
import { PageHeader } from "@ant-design/pro-layout";
import {
  SafetyCertificateOutlined,
  EyeOutlined,
  HomeOutlined,
  ShopOutlined,
  FileDoneOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import moment from "moment";

import CustomBreadcrumb from "../../../components/CustomBreadcrumb";
import NotificationWithIcon from "../../../components/NotificationWithIcon";
import {
  getSingleZatcaDevice,
  renewZatcaDeviceCsid,
} from "../../../network/network";
import { AppContext } from "../../../context/AppContextProvider";
import { handleApiError } from "../../../utils/errorHandler";
import {
  ZatcaDeviceStatus,
  ZatcaEnvironment,
} from "../../../configs/constants";

const getDeviceFromResponse = (response) => {
  const payload = response?.data?.data || response?.data || {};

  if (payload?.device) {
    return {
      ...payload.device,
      csid_valid: payload.csid_valid,
      invoices_count: payload.invoices_count,
    };
  }

  return payload || {};
};

const emptyText = (value) =>
  value === null || value === undefined || value === "" ? "—" : value;

const formatDate = (date, withTime = false) => {
  if (!date) return "—";

  const m = moment(date);
  if (!m.isValid()) return "—";

  return withTime ? m.format("YYYY-MM-DD hh:mm A") : m.format("YYYY-MM-DD");
};

const formatNumber = (value) => {
  if (value === null || value === undefined || value === "") return "—";

  const number = Number(value);
  return Number.isFinite(number) ? number.toLocaleString() : String(value);
};

const getStatusMeta = (status, t) => {
  const map = {
    [ZatcaDeviceStatus.Active]: {
      tagColor: "success",
      textColor: "var(--color-success)",
      label: t("active"),
    },
    [ZatcaDeviceStatus.CsidExpired]: {
      tagColor: "error",
      textColor: "var(--color-danger)",
      label: t("csid_expired"),
    },
    [ZatcaDeviceStatus.Inactive]: {
      tagColor: "default",
      textColor: "var(--color-text-muted)",
      label: t("inactive"),
    },
    [ZatcaDeviceStatus.NeedsReonboarding]: {
      tagColor: "warning",
      textColor: "var(--color-warning)",
      label: t("needs_reonboarding"),
    },
    [ZatcaDeviceStatus.PendingOnboarding]: {
      tagColor: "processing",
      textColor: "var(--color-info-hover)",
      label: t("pending_onboarding"),
    },
    [ZatcaDeviceStatus.CompliancePending]: {
      tagColor: "processing",
      textColor: "var(--color-info-hover)",
      label: t("compliance_pending"),
    },
    [ZatcaDeviceStatus.CompliancePassed]: {
      tagColor: "success",
      textColor: "var(--color-success)",
      label: t("compliance_passed"),
    },
  };

  return (
    map[status] || {
      tagColor: "default",
      textColor: "var(--color-text-muted)",
      label: status || "—",
    }
  );
};

const statusTag = (status, t) => {
  const entry = getStatusMeta(status, t);

  return <Tag color={entry.tagColor}>{entry.label}</Tag>;
};

const environmentLabel = (environment, t) => {
  const map = {
    [ZatcaEnvironment.Sandbox]: t("env_sandbox"),
    [ZatcaEnvironment.Simulation]: t("env_simulation"),
    [ZatcaEnvironment.Production]: t("env_production"),
  };

  return map[environment] || environment || "—";
};

const booleanTag = (value, t) => {
  if (value === true) return <Tag color="success">{t("yes")}</Tag>;
  if (value === false) return <Tag color="error">{t("no")}</Tag>;

  return "—";
};

const SummaryCard = ({ title, value, icon, valueStyle }) => (
  <Card>
    <Statistic
      title={title}
      value={value}
      prefix={icon}
      valueStyle={valueStyle}
    />
  </Card>
);

const ViewZatcaDevice = () => {
  const { t } = useTranslation();
  const { deviceId } = useParams();
  const navigate = useNavigate();
  const { appConfig } = useContext(AppContext);

  const [isDataLoading, setIsDataLoading] = useState(false);
  const [device, setDevice] = useState({});
  const [renewModalVisible, setRenewModalVisible] = useState(false);
  const [renewLoading, setRenewLoading] = useState(false);

  const [renewForm] = Form.useForm();

  const crProfile =
    device?.cr_profile || device?.branch?.zatca_cr_profile || {};

  const registeredByName = useMemo(() => {
    if (!device?.registered_by) return "—";

    return `${device.registered_by.first_name || ""} ${
      device.registered_by.last_name || ""
    }`.trim();
  }, [device]);

  const isOnboardingInProgress =
    device?.status === ZatcaDeviceStatus.PendingOnboarding ||
    device?.status === ZatcaDeviceStatus.CompliancePending;

  const deviceStatusMeta = useMemo(
    () => getStatusMeta(device?.status, t),
    [device?.status, t],
  );

  const fetchDeviceDetails = () => {
    setIsDataLoading(true);

    getSingleZatcaDevice(deviceId)
      .then((response) => {
        if (response?.data?.success === false) {
          handleApiError(response, t);
          return;
        }

        setDevice(getDeviceFromResponse(response));
      })
      .catch((error) => {
        handleApiError(error, t);
      })
      .finally(() => {
        setIsDataLoading(false);
      });
  };

  useEffect(() => {
    fetchDeviceDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId]);

  const shouldShowRenewButton = () => {
    if (isDataLoading || !device?.id) return false;

    if (device?.status === ZatcaDeviceStatus.CsidExpired) return true;

    const expiry = moment(device?.production_expires_at);
    if (!expiry.isValid()) return false;

    const daysLeft = expiry.diff(moment(), "days");

    return daysLeft >= 0 && daysLeft <= 30;
  };

  const handleRenewCsid = () => {
    renewForm.validateFields().then((values) => {
      setRenewLoading(true);

      renewZatcaDeviceCsid(deviceId, {
        otp: String(values.otp || "").replace(/\s+/g, ""),
      })
        .then((response) => {
          if (response?.data?.success === false) {
            handleApiError(response, t);
            return;
          }

          NotificationWithIcon(
            "success",
            t("zatca_csid_renewed_title"),
            t("zatca_csid_renewed_helper"),
          );

          setRenewModalVisible(false);
          renewForm.resetFields();
          fetchDeviceDetails();
        })
        .catch((error) => {
          handleApiError(error, t);
        })
        .finally(() => {
          setRenewLoading(false);
        });
    });
  };

  const breadcrumbRoutes = [
    {
      path: "/",
      breadcrumbName: t("home"),
      icon: <HomeOutlined />,
    },
    {
      path: "zatca-onboarding",
      breadcrumbName: t("zatca_onboarding"),
      icon: <SafetyCertificateOutlined />,
    },
    {
      path: "",
      breadcrumbName: t("zatca_device_details"),
      icon: <EyeOutlined />,
    },
  ];

  return (
    <>
      <Card className="app-mb-20">
        <CustomBreadcrumb menus={breadcrumbRoutes} />

        <PageHeader
          className="app-p-0"
          title={t("zatca_device_details")}
          onBack={() => navigate("/zatca-onboarding")}
          ghost={false}
          extra={
            shouldShowRenewButton()
              ? [
                  <Button
                    key="renew"
                    type="primary"
                    onClick={() => setRenewModalVisible(true)}
                  >
                    {t("renew_now")}
                  </Button>,
                ]
              : false
          }
        />
      </Card>

      <Spin spinning={isDataLoading}>
        {device?.status === ZatcaDeviceStatus.CsidExpired && (
          <Alert
            type="error"
            showIcon
            message={t("csid_expired")}
            description={t("csid_expired_alert_helper")}
            className="app-mb-20"
          />
        )}

        {device?.requires_reonboarding_at && (
          <Alert
            type="warning"
            showIcon
            message={t("zatca_reonboarding_required_title")}
            description={
              device?.stale_reason || t("zatca_reonboarding_required_helper")
            }
            className="app-mb-20"
          />
        )}

        {isOnboardingInProgress && (
          <Alert
            type="info"
            showIcon
            message={t("onboarding_in_progress")}
            className="app-mb-20"
          />
        )}

        <div
          style={{ direction: appConfig.direction === "rtl" ? "rtl" : "ltr" }}
        >
          <Row gutter={[16, 16]} className="app-mb-20">
            <Col xs={24} sm={12} lg={6}>
              <SummaryCard
                title={t("status")}
                value={deviceStatusMeta.label}
                icon={
                  <SafetyCertificateOutlined
                    style={{ color: deviceStatusMeta.textColor }}
                  />
                }
                valueStyle={{ color: deviceStatusMeta.textColor }}
              />
            </Col>

            <Col xs={24} sm={12} lg={6}>
              <SummaryCard
                title={t("environment")}
                value={environmentLabel(device?.environment, t)}
                icon={<ShopOutlined />}
              />
            </Col>

            <Col xs={24} sm={12} lg={6}>
              <SummaryCard
                title={t("invoices_count")}
                value={formatNumber(device?.invoices_count)}
                icon={<FileDoneOutlined />}
              />
            </Col>

            <Col xs={24} sm={12} lg={6}>
              <SummaryCard
                title={t("csid_expiry")}
                value={formatDate(device?.production_expires_at)}
                icon={<ClockCircleOutlined />}
              />
            </Col>
          </Row>

          <Card
            title={t("device_information")}
            className="app-mb-20"
            bodyStyle={{ paddingTop: 12 }}
          >
            <Descriptions
              bordered
              size="small"
              column={{ xs: 1, sm: 1, md: 2, lg: 2 }}
            >
              <Descriptions.Item label={t("device_name")}>
                {emptyText(device?.device_name)}
              </Descriptions.Item>

              <Descriptions.Item label={t("status")}>
                {statusTag(device?.status, t)}
              </Descriptions.Item>

              <Descriptions.Item label={t("branch")}>
                {emptyText(device?.branch?.name)}
              </Descriptions.Item>

              <Descriptions.Item label={t("branch_code")}>
                {emptyText(device?.branch?.code)}
              </Descriptions.Item>

              <Descriptions.Item label={t("environment")}>
                {environmentLabel(device?.environment, t)}
              </Descriptions.Item>

              <Descriptions.Item label={t("registered_by")}>
                {emptyText(registeredByName)}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          <Card
            title={t("zatca_information")}
            className="app-mb-20"
            bodyStyle={{ paddingTop: 12 }}
          >
            <Descriptions
              bordered
              size="small"
              column={{ xs: 1, sm: 1, md: 2, lg: 2 }}
            >
              <Descriptions.Item label={t("cr_number")}>
                {emptyText(device?.cr)}
              </Descriptions.Item>

              <Descriptions.Item label={t("csid_valid")}>
                {booleanTag(device?.csid_valid, t)}
              </Descriptions.Item>

              <Descriptions.Item label={t("invoices_count")}>
                {formatNumber(device?.invoices_count)}
              </Descriptions.Item>

              <Descriptions.Item label={t("last_icv")}>
                {formatNumber(device?.current_icv)}
              </Descriptions.Item>

              <Descriptions.Item label={t("onboarded_at")}>
                {formatDate(device?.onboarded_at, true)}
              </Descriptions.Item>

              <Descriptions.Item label={t("csid_expiry")}>
                {formatDate(device?.production_expires_at, true)}
              </Descriptions.Item>

              <Descriptions.Item label={t("requires_reonboarding_at")}>
                {formatDate(device?.requires_reonboarding_at, true)}
              </Descriptions.Item>

              <Descriptions.Item label={t("stale_reason")}>
                {emptyText(device?.stale_reason)}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          <Card title={t("seller_information")} bodyStyle={{ paddingTop: 12 }}>
            <Descriptions
              bordered
              size="small"
              column={{ xs: 1, sm: 1, md: 2, lg: 2 }}
            >
              <Descriptions.Item label={t("business_name_arabic")}>
                <span dir="rtl">
                  {emptyText(crProfile?.business_arabic_name)}
                </span>
              </Descriptions.Item>

              <Descriptions.Item label={t("cr_number")}>
                {emptyText(crProfile?.cr || device?.cr)}
              </Descriptions.Item>

              <Descriptions.Item label={t("street_name")}>
                {emptyText(crProfile?.street_name)}
              </Descriptions.Item>

              <Descriptions.Item label={t("building_number")}>
                {emptyText(crProfile?.building_number)}
              </Descriptions.Item>

              <Descriptions.Item label={t("plot_identification")}>
                {emptyText(crProfile?.plot_identification)}
              </Descriptions.Item>

              <Descriptions.Item label={t("district")}>
                {emptyText(crProfile?.district)}
              </Descriptions.Item>

              <Descriptions.Item label={t("city")}>
                {emptyText(crProfile?.city_name)}
              </Descriptions.Item>

              <Descriptions.Item label={t("postal_code")}>
                {emptyText(crProfile?.postal_zone)}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </div>
      </Spin>

      <Modal
        open={renewModalVisible}
        title={t("renew_csid")}
        onCancel={() => setRenewModalVisible(false)}
        maskClosable={false}
        footer={[
          <Button type="default"
            key="cancel"
            onClick={() => setRenewModalVisible(false)}
            disabled={renewLoading}
          >
            {t("cancel")}
          </Button>,
          <Button
            key="renew"
            type="primary"
            loading={renewLoading}
            onClick={handleRenewCsid}
          >
            {t("renew_now")}
          </Button>,
        ]}
      >
        <Alert
          type="info"
          showIcon
          message={t("get_your_otp")}
          description={t("zatca_get_otp_helper")}
          className="app-mb-16"
        />

        <Form form={renewForm} layout="vertical">
          <Form.Item
            name="otp"
            label={t("otp_6_digits")}
            rules={[
              { required: true, message: t("validate_otp_required") },
              { pattern: /^\d{6}$/, message: t("validate_otp_6_digits") },
            ]}
            getValueFromEvent={(event) =>
              String(event?.target?.value ?? "").replace(/\D+/g, "")
            }
          >
            <Input
              maxLength={6}
              inputMode="numeric"
              placeholder="● ● ● ● ● ●"
              style={{ letterSpacing: 4, textAlign: "center" }}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default ViewZatcaDevice;
